import styled, { css } from 'styled-components';
import { resetBtn } from 'styled-manila';
import { mpContainer } from 'core/components/Layout/Container';
import {
	getColor,
	typeScale,
	StyleVars,
} from 'core/utils';

// screenCjMinMaxContainer / numCards - gridGap * numGaps
const maxCardWidth = (1160 / 4) - (16 * 3);
// screenCjDesktopLg
const maxCardWidthXl = (1440 / 4) - (16 * 3);

export const baseText = css`
	color: ${getColor("gray")};
	line-height: ${StyleVars.lineHeightBase};
	letter-spacing: 0.2px;
	font-size: ${typeScale(2)};
`;

export const BasicGrid2x2 = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr;
	grid-gap: 0 16px;
`;

export const TextButton = styled.button.attrs({
	type: 'button'
})`
	${resetBtn}
	${baseText}
	color: ${props => props.isBlue ? getColor('darkTeal') : getColor('limedSpruceGray')};
	font-size: 14px;
	font-weight: 600;
	transition: all 0.3s ease-in-out;

	&:hover, &:focus, &:active {
		color: ${props => props.isBlue ? getColor('primaryBrandHover') : getColor('limedHover')};
	}

	&[disabled] {
		cursor: not-allowed;
		opacity: 0.8;
	}
`;

export const ChevronLink = styled.button`
	text-decoration: none;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	color: ${getColor('emperor')};
	display: flex;
	align-items: center;
	border: none;
	background: none;
	margin-bottom: 25px;
`;

export const Chevron = styled.div`
	width: 12px;
	height: 12px;
	display: block;
	border: 2px solid ${getColor('emperor')};
	transform: rotate(45deg);
	${props => props.left && css`
		border-top-color: transparent;
		border-right-color: transparent;
	`};
	${props => props.right && css`
		border-bottom-color: transparent;
		border-left-color: transparent;
	`};
	margin-right: 5px;
`;

export const FlexRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: ${props => props.centered ? 'center' : 'normal'};
	flex-wrap: wrap;
	${props => props.justifyCenter && css`
		justify-content: center;
	`};
	${props => props.spaceBetween && css`
		justify-content: space-between;
	`};
	${({ flex }) => flex && css`
		flex: ${flex};
	`};
	${({ textCentered }) => textCentered && css`
		text-align: center;
	`};
`;

export const FlexCol = styled.div`
	display: flex;
	flex-direction: column;
	align-items: ${props => props.centered ? 'center' : 'normal'};
	${({ flex }) => flex && css`
		flex: ${flex};
	`};
	${({ textCentered }) => textCentered && css`
		text-align: center;
	`};
`;

export const SectionHeading = styled.h3`
	font-size: 24px;
	font-weight: 600;
	line-height: 1.2;
	letter-spacing: 0.2px;
	color: ${getColor("gray")};
	margin: 0 0 32px;
`;

export const SubTitle = styled.h4`
	font-size: 19px;
	font-weight: 600;
	line-height: 1.2;
	letter-spacing: 0.2px;
	color: ${getColor("gray")};
	margin: 0 0 16px;
`;

export const Content = styled.p`
	${baseText}
	${({ large }) => !large && css`
		font-size: 14px;
	`}
	margin: 0 0 12px;

	ul, ol {
		padding: 0;
		padding-left: 1em;
	}
`;

export const ShowMore = styled.button`
	${resetBtn}
	color: ${getColor('primaryBrand')};
	font-weight: 600;

	${({ small }) => small && css`
		font-size: 14px;
	`}
`;

export const Separator = styled.hr`
	${mpContainer}
	border-top: 1px solid ${getColor('gray2')};
	margin-top: ${({ noMarginTop }) => noMarginTop ? '0' : '45px'};
	margin-bottom: ${({ noMarginBottom }) => noMarginBottom ? '0' : '45px'};
`;

export const SectionHeader = styled.div`
	text-align: center;
`;

export const SectionTitle = styled.h2`
	font-weight: 600;
	font-size: ${typeScale(2.25)};
	letter-spacing: -0.5px;
	line-height: ${typeScale(3)};
	margin-top: 0;
	margin-bottom: ${typeScale(3)};
	position: relative;
`;

export const SectionSubtitle = styled.div`
	font-weight: 600;
	font-size: ${typeScale(1.5)};
	text-transform: uppercase;
	margin-bottom: ${typeScale(0.5)};
`;

export const ListingItemsContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(${maxCardWidth}px, 1fr));
	grid-gap: 16px;
	-webkit-overflow-scrolling: touch;

	${props => props.large && css`
		grid-template-columns: repeat(auto-fill, minmax(${maxCardWidthXl}px, 1fr));
	`}

	@media(max-width: ${StyleVars.screenMd}) {
		grid-template-columns: 1fr 1fr;
		grid-gap: 16px;
		margin-right: 16px;
	}
`;

export const AccountHeader = styled.h2`
	${baseText}
	margin-top: 0;
	margin-bottom: ${StyleVars.defaultSpacingUnit * 2}px;
	font-weight: 600;
	font-size: ${typeScale(3.5)};
`;

export const FlairTag = styled.div`
	margin-top: 8px;
	font-size: 10px;
	font-weight: 700;
	display: inline-block;
	margin-right: 14px;
	line-height: 15px;
	border-radius: 2px;
	padding: 2px 4px;

	&.low-stock {
		color: ${getColor("deepOrange")};
		background: ${getColor("lightYellow")};
	}

	&.free-shipping, &.ships-fast {
		color: ${getColor("watercourse")};
		background: ${getColor("scandal")};
	}

	&.order-soon {
		color: ${getColor("oldBrick")};
		background: ${getColor("lightRed")};
	}

	&.top-gift {
		color: ${getColor("white")};
		background: ${getColor("holidayRed")};
	}

	@media (min-width: ${StyleVars.screenSm}) {
		margin-right: 14px;
		margin-top:	16px;
	}
`;

export const TagLink = styled.a`
	display: block;
	text-decoration: none;
	text-align: center;
	margin-right: 16px;
	margin-bottom: 16px;
	padding: 16px 24px;
	font-size: 14px;
	line-height: 18px;
	letter-spacing: 0.2px;
	color: ${getColor('gray')};
	border-radius: 24px;
	background-color: ${getColor('concreteGray')};
`;
